import type { AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "axios";
import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  CalendarEvent,
  CalendarClinician,
  PatientCalendarEvent,
  CalendarEventIdentifier,
  CreateAppointmentData,
  CreateRecurringSeriesResponse,
  EmptyResponse,
  FilterByQuery,
  IncludeQuery,
  MyDay,
  PaginationQuery,
  PaginationResponse,
  PatientIdentifier,
  PlannerResource,
  Response,
  SortByQuery,
  updatedAppointmentTreatmentId,
} from "~/types/api";

export function createCalendarApi({
  getRequest,
  postRequest,
  putRequest,
  patchRequest,
  deleteRequest,
  getPaginationRequest,
}: ApiRequestHelpers) {
  const calendarClinicians = (locationId: number, pagination?: PaginationQuery, filters?: FilterByQuery) =>
    getPaginationRequest<CalendarClinician>(
      `/calendar/locations/${locationId}/clinicians`,
      pagination,
      undefined,
      undefined,
      filters,
    );

  /** Fetches a paginated list of calendar events. */
  const calendarEvents = (
    pagination?: PaginationQuery,
    includes?: IncludeQuery,
    sortBy?: SortByQuery,
    filterBy?: FilterByQuery,
  ) => {
    return getPaginationRequest<CalendarEvent>("/calendar/events", pagination, sortBy, includes, filterBy);
  };

  /** Fetches a single calendar event by ID. */
  const calendarEvent = (eventId: CalendarEventIdentifier) =>
    getRequest<Response<CalendarEvent>>(`/calendar/events/${eventId}`);

  /** Creates a new calendar event, optionally as a recurring series. */
  const createEvent = (data: CreateAppointmentData) => {
    if (data.is_recurring) {
      return postRequest<CreateRecurringSeriesResponse>("/calendar/events", data);
    }
    return postRequest<Response<CalendarEvent>>("/calendar/events", data);
  };

  /** Updates an existing calendar event, optionally applying changes to the entire series. */
  const updateEvent = (
    eventId: CalendarEventIdentifier,
    data: CreateAppointmentData,
    updateEntireSeries: boolean = false,
  ) => {
    const url = `/calendar/events/${eventId}${updateEntireSeries ? "?update_entire_series=true" : ""}`;
    return putRequest<Response<CalendarEvent>>(url, data);
  };

  /** Updates the treatment linked to an appointment. */
  const updateAppointmentTreatment = (appointmentId: number, treatmentId: number) =>
    patchRequest<Response<updatedAppointmentTreatmentId>>(`/appointments/${appointmentId}/treatment`, {
      treatment_id: treatmentId,
    });

  /** Deletes a calendar event, optionally deleting the entire series and notifying via email. */
  const deleteEvent = (
    eventId: CalendarEventIdentifier,
    sendEmail: boolean = true,
    deleteEntireSeries: boolean = false,
    comment?: string,
  ) => {
    const params = new URLSearchParams();
    params.append("send_email", sendEmail.toString());
    if (deleteEntireSeries) {
      params.append("delete_entire_series", "true");
    }
    const config: AxiosRequestConfig = {};
    if (comment) {
      config.data = { comment };
    }
    return deleteRequest<EmptyResponse>(`/calendar/events/${eventId}?${params.toString()}`, config);
  };

  /** Fetches the current user's appointments for a given date. */
  const myDay = (formattedDate: string, include: string | null) =>
    getRequest<PaginationResponse<MyDay>>(
      `/calendar/events/my-day?date=${formattedDate}&include[]=${include ?? "appointment"}&include[]=appointment.calendarEvent.location`,
    );

  /**
   * Fetches calendar events for a specific patient.
   * Without an explicit sortBy this defaults to oldest-first with a fixed page size: for a
   * patient with more than a page of matching events, a recent (e.g. today's) event can be
   * pushed off the first page entirely and never come back. Pass sortBy (e.g. start_at desc)
   * whenever the caller needs a specific recent event to reliably be present in the result.
   */
  const patientCalendarEvents = (patientId: PatientIdentifier, filterBy: FilterByQuery, sortBy?: SortByQuery) =>
    getPaginationRequest<PatientCalendarEvent>(
      `/calendar/patients/${patientId}/events`,
      undefined,
      sortBy,
      undefined,
      filterBy,
    );

  /** Fetches planner data for a date range, location, and optional set of practitioners. */
  const planner = (from: string, to: string, locationId: number, userIds?: number[]) => {
    const params = new URLSearchParams({
      from,
      to,
      location_id: locationId.toString(),
    });

    if (userIds) {
      params.append("user_ids", userIds.join(","));
    }

    return getRequest<PlannerResource>(`/planner?${params.toString()}`);
  };

  /** Creates an appointment via the public embed endpoint (BFF). */
  const createAppointmentEmbed = (
    embedId: string,
    data: {
      location_id: number;
      user_id: number;
      product_id: number;
      appointment: {
        start_date: string;
        end_date: string;
      };
      patient: {
        first_name: string;
        infix?: string;
        last_name: string;
        initials: string;
        gender: string;
        dob: string;
        email: string;
        timezone: string;
        phone_number: string;
        citizen_service_number?: string;
        address: {
          street: string;
          street_number: string;
          street_number_addition?: string;
          postal_code: string;
          city: string;
          country: string;
        };
      };
    },
  ) => {
    const { public: config } = useRuntimeConfig();
    const baseUrl = (config.api.baseUrl ?? "").replace("/api/v1", "");
    const client = axios.create({
      baseURL: baseUrl,
      headers: {
        common: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
      },
    });

    const formData = new URLSearchParams();
    formData.append("location_id", data.location_id.toString());
    formData.append("user_id", data.user_id.toString());
    formData.append("product_id", data.product_id.toString());
    formData.append("appointment[start_date]", data.appointment.start_date);
    formData.append("appointment[end_date]", data.appointment.end_date);
    formData.append("patient[first_name]", data.patient.first_name);
    if (data.patient.infix) {
      formData.append("patient[infix]", data.patient.infix);
    }
    formData.append("patient[last_name]", data.patient.last_name);
    formData.append("patient[initials]", data.patient.initials);
    formData.append("patient[gender]", data.patient.gender);
    formData.append("patient[dob]", data.patient.dob);
    formData.append("patient[email]", data.patient.email);
    formData.append("patient[timezone]", data.patient.timezone);
    formData.append("patient[phone_number]", data.patient.phone_number);
    if (data.patient.citizen_service_number) {
      formData.append("patient[citizen_service_number]", data.patient.citizen_service_number);
    }
    formData.append("patient[address][street]", data.patient.address.street);
    formData.append("patient[address][street_number]", data.patient.address.street_number);
    if (data.patient.address.street_number_addition) {
      formData.append("patient[address][street_number_addition]", data.patient.address.street_number_addition);
    }
    formData.append("patient[address][postal_code]", data.patient.address.postal_code);
    formData.append("patient[address][city]", data.patient.address.city);
    formData.append("patient[address][country]", data.patient.address.country);

    return client.post(`/bff/${embedId}`, formData).then((response: AxiosResponse) => response.data);
  };

  return {
    calendarClinicians,
    calendarEvents,
    calendarEvent,
    createEvent,
    updateEvent,
    updateAppointmentTreatment,
    deleteEvent,
    myDay,
    patientCalendarEvents,
    planner,
    createAppointmentEmbed,
  };
}

export type CalendarApi = ReturnType<typeof createCalendarApi>;
